"use client";

import { getImageProps } from "next/image";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { CTAButton } from "@/shadcn/ui/button";
import { competitorRichText } from "./richtext";
import ComparisonTable from "./ComparisonTable";
import { PageCompetitorSchema } from "@/sanity/pages/PageCompetitorTemplate";
import heroBg from "@/public/competitor/hero-bg.jpg";
import heroBgMobile from "@/public/competitor/hero-bg-mobile.jpg";

const ease = [0.25, 0.1, 0.25, 1] as const;

const bgProps = { alt: "", fill: true, priority: true, sizes: "100vw" } as const;
const { props: { srcSet: mobileBgSrcSet } } = getImageProps({ ...bgProps, src: heroBgMobile });
const { props: desktopBg } = getImageProps({ ...bgProps, src: heroBg });

export default function CompetitorHero({ data }: { data: PageCompetitorSchema }) {
  const rows = data.heroComparisonRows ?? [];

  return (
    <section className="relative w-full overflow-hidden bg-white px-[2rem] [text-wrap:pretty] pt-[6rem] pb-[8rem] lg:px-0 lg:pt-[11.25rem] lg:pb-[15rem]">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 max-lg:bottom-0 lg:aspect-[1920/1218]">
        <picture>
          <source media="(max-width: 992px)" srcSet={mobileBgSrcSet} />
          <img {...desktopBg} className="object-cover object-top" />
        </picture>
      </div>

      <div className="relative flex flex-col items-center gap-[5rem] lg:gap-[5.875rem]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="flex w-[min(120.5rem,100%)] flex-col items-center gap-[4rem]">
          <div className="flex w-full flex-col items-center gap-[2.5rem] lg:gap-[2rem]">
            {data.heroChip && (
              <p className="mb-0 text-center font-blacker text-[2rem] leading-[1.4] text-black italic">{data.heroChip}</p>
            )}
            {data.heroTitle && (
              <div className="richtext gap-[2.5rem] max-lg:[text-wrap:balance] text-center prose-headings:my-0 prose-headings:font-geist max-lg:prose-headings:leading-[1.04] lg:prose-headings:leading-[1.15] prose-headings:text-black max-lg:prose-headings:text-[5rem] max-lg:prose-headings:font-medium max-lg:prose-headings:tracking-[-0.25rem] lg:prose-headings:font-semibold lg:prose-headings:text-[7rem] lg:prose-headings:tracking-[-0.35rem] prose-p:my-0 prose-p:font-geist prose-p:!text-[2.25rem] prose-p:leading-[1.5] max-lg:prose-p:font-normal max-lg:prose-p:!text-black/70 lg:prose-p:font-medium lg:prose-p:!text-black/80 [--hl-wash:4.25rem] lg:[--hl-wash:5.75rem] [--hl-tracking:-0.03em] [--hl-ml:2.125rem] [&_:is(h1,h2)_span]:leading-[0] max-lg:[&_:is(h1,h2)>span]:!leading-[1.15]">
                <PortableText value={data.heroTitle} components={competitorRichText} />
              </div>
            )}
          </div>
          {data.heroCTA && (
            <CTAButton data={data.heroCTA} className="w-full [&_.button]:min-h-[5.75rem] [&_.button]:px-[5rem] lg:w-fit" />
          )}
        </motion.div>

        {rows.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease, delay: 0.15 }}
            className="flex w-full flex-col items-center">
            <ComparisonTable rows={rows} competitorLabel={data.heroCompetitorLabel} />
          </motion.div>
        )}

        {data.heroBottomCTA && (
          <CTAButton data={data.heroBottomCTA} className="w-full [&_.button]:min-h-[5.75rem] [&_.button]:px-[5rem] max-lg:-mt-[0.75rem] lg:w-fit" />
        )}
      </div>
    </section>
  );
}
